import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Outlet, useLocation } from "react-router-dom";
import { usePortalLanguage, localeFromPublicPath } from "@/contexts/PortalLanguageContext";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import ScrollToTop from "@/components/ScrollToTop";
import RouteProgress from "@/components/RouteProgress";
import CookieBanner from "@/components/CookieBanner";
import SeoHead from "@/components/SeoHead";

export default function AppLayout() {
  const { pathname } = useLocation();
  const { language, setLanguage } = usePortalLanguage();

  // Keep body copy in the language of the URL being viewed. Scoped to this
  // layout so it applies to the public marketing surface only — the portal and
  // admin keep using the visitor's stored preference. persist:false so browsing
  // the public site never rewrites that preference.
  useEffect(() => {
    const urlLocale = localeFromPublicPath(pathname);
    if (urlLocale && urlLocale !== language) {
      setLanguage(urlLocale, { persist: false });
    }
  }, [pathname, language, setLanguage]);

  return (
    <>
      {/* Site-wide head FALLBACK layer.
          Every tag here is overridden by a page's own <Helmet> under react-helmet's
          nesting rules, so it only shows through on pages that set nothing. This is
          the correct home for these defaults — index.html is not, because helmet
          appends meta tags rather than replacing them and anything declared there
          becomes a permanent duplicate. twitter:card and twitter:image moved here
          from index.html for exactly that reason. */}
      <Helmet>
        <title>Fyrris – Design your moment</title>
        <meta
          name="description"
          content="Fyrris helps you design and visualize your perfect outdoor space with AR – connect with local retailers and experience the future of fire, patio, and lifestyle."
        />
        <meta name="keywords" content="Fyrris, AR, patio, firepit, outdoor design, retailers, BBQ, 3D visualization" />
        <meta name="author" content="Fyrris Inc." />
        <meta property="og:title" content="Fyrris – Design your moment" />
        <meta property="og:description" content="Discover AR-powered patio and fireplace design with Fyrris." />
        <meta property="og:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.fyrris.com" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.fyrris.com/fyrris-social-banner.png" />
      </Helmet>

      {/* Canonical + hreflang for the current route. */}
      <SeoHead />

      {/* Skip to main content — screen-reader only; appears on keyboard focus */}
      <a
        href="#main-content"
        style={{ position: "absolute", left: "-9999px" }}
        onFocus={(e) => {
          e.currentTarget.style.left = "1rem";
          e.currentTarget.style.top = "1rem";
          e.currentTarget.style.position = "fixed";
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = "-9999px";
          e.currentTarget.style.position = "absolute";
        }}
        className="z-50 px-4 py-2 bg-[#FF6B35] text-white rounded-full"
      >
        Skip to main content
      </a>

      {/* 🌐 Your main site layout */}
      <Navbar />
      <main id="main-content" className="pt-16 mb-0">
        <Outlet />
      </main>
      <Footer />
      <CookieBanner />
      <ScrollToTop />
      <RouteProgress />
    </>
  );
}
